import { useCallback, useState } from "react";
import {
  useCreateEventCommentMutation,
  useEventArchiveMutation,
  useEventAskValidationMutation,
  useEventReopenMutation,
  useFetchEventCommentsQuery,
} from "../../apiSlice";
import { notification } from "antd";
import { getErrorMessage } from "../../../../utils/errors/errors";
import {
  FetchEventsQuery,
  RefetchEventQuery,
} from "../../../../types/reactQueries";
import { NavigateFunction } from "react-router-dom";
import config from "../../../../config";
import { useAppSelector } from "../../../redux-store/hooks";
import { CommentPostValues } from "../../../../types/comments";

type Options = {
  fetchEvents?: FetchEventsQuery;
  fetchEvent?: RefetchEventQuery;
  navigate?: NavigateFunction;
  eventId?: number;
};

export const useCrudEvent = ({
  fetchEvents,
  fetchEvent,
  navigate,
  eventId,
}: Options) => {
  /**
   * Archive/Delete
   */
  const [deleteEvent, { isLoading: isDeleting }] = useEventArchiveMutation();
  const onDelete = useCallback(
    async (id: number) => {
      try {
        console.log("deleteEvent id", id);
        await deleteEvent(id).unwrap();
        // Success notification
        notification.success({
          message: "ÃvÃ©nement supprimÃ©",
          description: `L'Ã©vÃ©nement a Ã©tÃ© supprimÃ© avec succÃ¨s.`,
        });
        if (fetchEvents) {
          await fetchEvents("").unwrap();
        } else {
          console.warn("The 'onDelete' fn needs 'fetchEvents' to be defined");
        }
      } catch (error) {
        console.error("deleteEvent error", error);
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [deleteEvent, fetchEvents],
  );

  const [archiveEventRequest, { isLoading: isArchiving }] =
    useEventArchiveMutation();
  const archiveEvent = useCallback(
    async (id: number) => {
      try {
        await archiveEventRequest(Number(id)).unwrap();
        notification.success({
          message: "L'Ã©vÃ©nement a Ã©tÃ© archivÃ© avec succÃ¨s",
        });
        if (navigate) {
          navigate("/events");
        } else {
          console.warn("The 'archiveEvent' fn needs 'navigate' to be defined");
        }
      } catch (error) {
        console.error("archiveEvent error", error);
      }
    },
    [archiveEventRequest, navigate],
  );

  /**
   *
   */

  /**
   * Ask validation
   */
  const [askValidation, { isLoading: isAskingValidation }] =
    useEventAskValidationMutation();
  const onAskValidation = useCallback(
    async (id: number) => {
      try {
        console.log("askValidation id", id);
        await askValidation(id).unwrap();
        // Success notification
        notification.success({
          message: "Demande de validation effectuÃ©e.",
        });
        if (fetchEvent) {
          await fetchEvent().unwrap();
        } else {
          console.warn(
            "The 'askValidation' fn needs 'fetchEvent' to be defined",
          );
        }
      } catch (error) {
        console.error("askValidation error", error);
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [askValidation, fetchEvent],
  );

  /**
   * Comments
   */
  const { data: comments, refetch: refetchComments } = useFetchEventCommentsQuery(
    eventId as number,
    { skip: !eventId },
  );
  const [createCommentRequest, { isLoading: isCreatingComment }] =
    useCreateEventCommentMutation();
  const createComment = useCallback(
    async (values: Pick<CommentPostValues, 'description'>) => {
      if (!eventId) return;
      try {
        await createCommentRequest({
          description: values.description,
          commentType: 'EVENT',
          event: eventId,
        }).unwrap();
        notification.success({ message: "Commentaire ajoutÃ©." });
        refetchComments();
      } catch (error) {
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [createCommentRequest, eventId, refetchComments],
  );

  /**
   * Export PDF
   */
  const accessToken = useAppSelector((state) => state.auth.user?.access_token);
  const [isExporting, setIsExporting] = useState(false);
  const exportPdf = useCallback(
    async (id: number) => {
      setIsExporting(true);
      try {
        const response = await fetch(`${config.apiUrl}/evt/events/${id}/export`, {
          headers: { Authorization: `Bearer ${accessToken ?? ""}` },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.error || `Erreur ${response.status}`);
        }

        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = `evenement_${id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);

        notification.success({ message: "Export PDF tÃ©lÃ©chargÃ© avec succÃ¨s." });
      } catch (error) {
        notification.error({
          message: "Ãchec de l'export PDF",
          description: getErrorMessage(error),
        });
      } finally {
        setIsExporting(false);
      }
    },
    [accessToken],
  );

  /**
   * Reopen
   */
  const [reopen, { isLoading: isReopening }] = useEventReopenMutation();
  const onReopen = useCallback(
    async (id: number) => {
      try {
        console.log("reopen id", id);
        await reopen(id).unwrap();
        // Success notification
        notification.success({
          message: "ÃvÃ©nement rÃ©ouvert",
          description: `L'Ã©vÃ©nement a Ã©tÃ© rÃ©ouvert avec succÃ¨s.`,
        });
        if (fetchEvents) {
          await fetchEvents("").unwrap();
        } else {
          console.warn("The 'reopen' fn needs 'fetchEvents' to be defined");
        }
      } catch (error) {
        console.error("reopen error", error);
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [reopen, fetchEvents],
  );

  return {
    // Delete/archive
    onDelete,
    isDeleting,
    archiveEvent,
    isArchiving,
    // Ask validation
    onAskValidation,
    isAskingValidation,
    // Reopen
    onReopen,
    isReopening,
    // Export PDF
    exportPdf,
    isExporting,
    // Comments
    comments,
    createComment,
    isCreatingComment,
  };
};
